/**
 * CognaraRaidPushInline — inline mini-editor next to each Cognara bullet
 * that lets a user push the bullet as a RAID item (Risk / Issue / Action
 * / Dependency) with edit capability (title, category, priority, owner,
 * due date) before commit.  On success shows a green "✓ RAID-nnnn" chip.
 */
import { useState } from 'react';
import { Plus, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const CATEGORY_BY_SECTION = {
  cautions:       'RISK',
  monitoring:     'RISK',
  action_plan:    'ACTION',
  catch_up_plan:  'ACTION',
  cross_impact:   'ISSUE',
};
const CATEGORIES = ['RISK', 'ISSUE', 'ACTION', 'DEPENDENCY'];
const PRIORITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

export default function CognaraRaidPushInline({
  projectId, section, bullet, task, riskLevel,
  // Optional overrides: kind + code + name are used when the source is
  // NOT a schedule task (e.g. CBS row).
  sourceKind = 'schedule-task', sourceCode, sourceName,
  raidCategory = 'SCHEDULE',
}) {
  const [editing, setEditing] = useState(false);
  const [pushed, setPushed]   = useState(null);        // { raidNumber }
  const [busy, setBusy]       = useState(false);

  const defaultCategory =
    (riskLevel === 'HIGH' && section === 'cautions') ? 'ISSUE'
    : CATEGORY_BY_SECTION[section] || 'RISK';
  const defaultPriority =
    riskLevel === 'HIGH' ? 'HIGH' : riskLevel === 'LOW' ? 'LOW' : 'MEDIUM';

  const [title, setTitle]       = useState(bullet);
  const [category, setCategory] = useState(defaultCategory);
  const [priority, setPriority] = useState(defaultPriority);
  const [owner, setOwner]       = useState('');
  const [due, setDue]           = useState('');

  const submit = async () => {
    if (!title.trim()) { toast.error('Title required'); return; }
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        description: `Sourced from Cognara ${section.replace('_', ' ')} for ${sourceKind === 'cbs-row' ? 'CBS ' + (sourceCode || '') : 'task ' + (task?.wbs_code || '')} · ${sourceName || task?.name || ''}`,
        category,
        raidCategory,
        priority,
        ownerName: owner || undefined,
        dueDate: due || undefined,
        area: (task?.wbs_path || []).slice(0, 2).map(n => n.name).filter(Boolean).join(' › ') || sourceCode || undefined,
        tags: ['cognara', section, (sourceCode || task?.wbs_code || '').toLowerCase()].filter(Boolean),
        linkedItems: [{
          kind: sourceKind,
          id:   task?.id || sourceCode,
          code: sourceCode || task?.wbs_code,
          name: sourceName || task?.name,
        }],
      };
      // Section-specific bucket enrichment
      if (category === 'RISK') {
        const pi = { HIGH: 4, MEDIUM: 3, LOW: 2, CRITICAL: 5 }[priority] || 3;
        payload.riskSpecific = {
          probabilityScore: pi,
          impactScore:      pi,
          riskCategory:     'SCHEDULE',
          responseStrategy: 'MITIGATE',
          responseDescription: `Follow-up on Cognara ${section.replace('_', ' ')}`,
        };
      } else if (category === 'ISSUE') {
        payload.issueSpecific = {
          reportedBy: 'Cognara 5D Analysis',
          rootCause: `Auto-derived from schedule 5D drilldown (${section})`,
        };
      } else if (category === 'ACTION') {
        payload.actionSpecific = { assignedTo: owner || 'Unassigned' };
      }
      const r = await api.post(`/v1/projects/${projectId}/raid/items`, payload);
      const raidNumber = r?.data?.raidNumber || r?.data?.item?.raidNumber || r?.data?.number;
      setPushed({ raidNumber: raidNumber || 'created' });
      setEditing(false);
      toast.success(`Pushed to RAID${raidNumber ? ` — ${raidNumber}` : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'RAID push failed');
    } finally { setBusy(false); }
  };

  if (pushed) {
    return (
      <span className="inline-flex items-center gap-1 text-[9.5px] px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 ml-1"
            data-testid={`cognara-raid-pushed-${section}`}>
        <Check className="w-2.5 h-2.5" /> RAID {pushed.raidNumber}
      </span>
    );
  }

  if (!editing) {
    return (
      <button onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        className="inline-flex items-center gap-0.5 text-[9.5px] px-1.5 py-0.5 rounded-full bg-slate-100 hover:bg-fuchsia-100 text-slate-500 hover:text-fuchsia-700 dark:bg-slate-800 dark:hover:bg-fuchsia-500/20 dark:text-slate-400 dark:hover:text-fuchsia-300 ml-1"
        title="Add to RAID log"
        data-testid={`cognara-raid-add-${section}`}
      >
        <Plus className="w-2.5 h-2.5" /> RAID
      </button>
    );
  }

  return (
    <div className="mt-1 p-2 rounded-md border border-fuchsia-200 dark:border-fuchsia-500/40 bg-white dark:bg-[#0a0f1e] space-y-1.5"
         onClick={(e) => e.stopPropagation()}
         data-testid={`cognara-raid-editor-${section}`}>
      <input value={title} onChange={e => setTitle(e.target.value)}
             placeholder="RAID title"
             className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-transparent text-[10.5px] font-medium"
             data-testid={`cognara-raid-title-${section}`} />
      <div className="grid grid-cols-2 gap-1.5 text-[10px]">
        <select value={category} onChange={e => setCategory(e.target.value)}
                className="px-1.5 py-1 rounded border border-slate-300 dark:border-slate-600 bg-transparent"
                data-testid={`cognara-raid-category-${section}`}>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={priority} onChange={e => setPriority(e.target.value)}
                className="px-1.5 py-1 rounded border border-slate-300 dark:border-slate-600 bg-transparent"
                data-testid={`cognara-raid-priority-${section}`}>
          {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <input value={owner} onChange={e => setOwner(e.target.value)}
               placeholder="Owner"
               className="px-1.5 py-1 rounded border border-slate-300 dark:border-slate-600 bg-transparent"
               data-testid={`cognara-raid-owner-${section}`} />
        <input type="date" value={due} onChange={e => setDue(e.target.value)}
               className="px-1.5 py-1 rounded border border-slate-300 dark:border-slate-600 bg-transparent"
               data-testid={`cognara-raid-due-${section}`} />
      </div>
      <div className="flex items-center justify-end gap-1.5">
        <button onClick={() => setEditing(false)} disabled={busy}
                className="text-[10px] px-2 py-1 rounded border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 disabled:opacity-40"
                data-testid={`cognara-raid-cancel-${section}`}>Cancel</button>
        <button onClick={submit} disabled={busy}
                className="text-[10px] px-2 py-1 rounded bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-semibold flex items-center gap-1 disabled:opacity-40"
                data-testid={`cognara-raid-submit-${section}`}>
          {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin"/> : <Plus className="w-2.5 h-2.5"/>}
          Push to RAID
        </button>
      </div>
    </div>
  );
}
